/**
 * Menu in Page Header.
 */
export class Header {

	#breakpointMobile = 767;

	#breakpointTablet = 1279;

	#mqMobile;

	#mqTablet;

	#menuTrigger;

	#menuEl;

	#navEl;

	#megaMenuTriggers;

	#menuTriggers;

	#megaMenuDropdowns;

	#languageSwitcher;

	#extrasEl;

	#headerEl;

	#scrollPosition;

	#isMobile = false;

	#isTablet = false;

	#isScrolling = false;

	// bound handlers
	#boundBreakpointChange;

	#boundBodyClick;

	#boundKeyDown;

	#boundMegaMenuClick;

	#boundMenuClick;

	#boundMenuTrigger;

	#boundScroll;

	#boundResize;


	/**
	 * Constructor function for initializing a header component.
	 *
	 * @param {Element} block - The block element containing the header components.
	 */
	constructor ( block ) {

		this.#headerEl = block;
		if ( !this.#headerEl ) {

			return;

		}

		this.#menuEl = this.#headerEl.querySelector( '.js-menu-header' );
		if ( !this.#menuEl ) {

			return;

		}

		this.#menuTrigger = this.#headerEl.querySelector( '.js-page-header-menu-trigger' );
		this.#navEl = this.#headerEl.querySelector( '.p-header__nav' );
		this.#languageSwitcher = this.#headerEl.querySelector( '.p-header__language-switcher' );
		this.#extrasEl = this.#headerEl.querySelector( '.p-header__extras' );
		this.#megaMenuTriggers = this.#headerEl.querySelectorAll( '.js-megamenu-dropdown' );
		this.#menuTriggers = this.#headerEl.querySelectorAll( 'a.menu-item__link' );
		this.#megaMenuDropdowns = this.#menuEl.querySelectorAll( '.js-megamenu-dropdown-item' );

		this.#mqMobile = window.matchMedia( `(max-width: ${this.#breakpointMobile}px)` );
		this.#mqTablet = window.matchMedia(
			`(min-width: ${this.#breakpointMobile + 1}px) and (max-width: ${this.#breakpointTablet}px)`
		);

		// bind handlers once
		this.#boundBreakpointChange = this.#handleBreakpointChange.bind( this );
		this.#boundBodyClick = this.#handleBodyClick.bind( this );
		this.#boundKeyDown = this.#handleKeyDown.bind( this );
		this.#boundMegaMenuClick = this.#handleMegaMenuClick.bind( this );
		this.#boundMenuClick = this.#handleMenuClick.bind( this );
		this.#boundMenuTrigger = this.#handleMenuTrigger.bind( this );
		this.#boundScroll = this.#handleScrollClass.bind( this );
		this.#boundResize = this.#debounce(
			this.#setBodyPadding.bind( this ),
			150
		);

		this.#init();

	}

	/**
	 * Initializes event listeners and handling methods for the webpage.
	 * Binds event handlers to specific elements on the page.
	 * Also sets up functionality for responsive design changes.
	 * Executes additional methods related to page setup.
	 *
	 * @return {void}
	 */
	#init () {

		document.body.addEventListener( 'click', this.#boundBodyClick );
		document.addEventListener( 'keydown', this.#boundKeyDown );
		this.#headerEl.addEventListener( 'click', this.#boundMegaMenuClick );
		this.#headerEl.addEventListener( 'click', this.#boundMenuClick );
		this.#menuTrigger.addEventListener( 'click', this.#boundMenuTrigger );
		window.addEventListener( 'scroll', this.#boundScroll );
		window.addEventListener( 'resize', this.#boundResize );
		this.#mqMobile.addEventListener( 'change', this.#boundBreakpointChange );
		this.#mqTablet.addEventListener( 'change', this.#boundBreakpointChange );
		this.#handleBreakpointChange();
		this.#moveLanguageSwitcher();
		this.#moveExtraButtons();

		/**
		 * A function that handles actions to be taken when the window is ready.
		 * This function uses requestAnimationFrame to improve performance.
		 * It calls internal methods to set body padding and language switcher height.
		 */
		const onReady = () => {

			requestAnimationFrame( () => {

				this.#setBodyPadding();
				this.#setLanguageSwitcherHeight();

			} );

		};

		if ( document.readyState === 'loading' ) {

			document.addEventListener( 'DOMContentLoaded', onReady );

		} else {

			onReady();

		}

	}

	/**
	 * Handle the change in breakpoint by updating the state properties isMobile and isTablet accordingly.
	 *
	 * @return {void}
	 */
	#handleBreakpointChange () {

		const wasMobile = this.#isMobile;
		const wasTablet = this.#isTablet;

		this.#isMobile = this.#mqMobile.matches;
		this.#isTablet = !this.#isMobile && this.#mqTablet.matches;

		if ( wasMobile !== this.#isMobile || wasTablet !== this.#isTablet ) {

			this.#onLayoutChange();

		}

	}

	/**
	 * Handles layout change by performing necessary actions such as moving elements, setting heights, and closing menus.
	 * @function onLayoutChange
	 * @return {void}
	 */
	#onLayoutChange () {

		this.#moveLanguageSwitcher();
		this.#setLanguageSwitcherHeight();
		this.#moveExtraButtons();
		this.#closeMenu();
		this.#closeAllMegaMenus();

	}

	/**
	 * Handle the click event on a menu item.
	 *
	 * @param {Event} event - The click event triggered by clicking on a menu item.
	 * @return {void}
	 */
	#handleMenuClick ( event ) {

		const menuItem = event.target.closest( 'a.menu-item__link' );
		if( !menuItem ) {

			return;

		}
		this.#closeAllMegaMenus();

	}

	/**
	 * Handles click event on mega menu button.
	 *
	 * @param {Event} event - The click event triggered on the mega menu button.
	 *
	 * @return {void}
	 */
	#handleMegaMenuClick ( event ) {

		const toggleButton = event.target.closest( '.js-megamenu-dropdown' );
		if ( !toggleButton ) {

			return;

		}

		event.preventDefault();
		event.stopPropagation();

		const navItem = toggleButton.closest( '.menu-item-has-children' );
		const dropdown = navItem?.querySelector( '.js-megamenu-dropdown-item' );
		const isActive = toggleButton.classList.contains( 'menu-item__link--active' );

		this.#closeAllMegaMenus();

		if ( !isActive && dropdown ) {

			toggleButton.classList.add( 'menu-item__link--active' );
			dropdown.classList.add( 'b-nav-dropdown--active' );
			dropdown.style.maxHeight = `${dropdown.scrollHeight}px`;

		}

	}

	/**
	 * Closes all mega menus by removing active classes and setting max height to 0.
	 *
	 * @return {void}
	 */
	#closeAllMegaMenus () {

		this.#megaMenuTriggers.forEach( btn =>
			btn.classList.remove( 'menu-item__link--active' )
		);

		this.#megaMenuDropdowns.forEach( drop => {

			drop.classList.remove( 'b-nav-dropdown--active' );
			drop.style.maxHeight = '0';

		} );

	}

	/**
	 * Moves the language switcher element to a different parent based on device type and element containment.
	 *
	 * @return {void}
	 */
	#moveLanguageSwitcher () {

		if ( !this.#languageSwitcher ) {

			return;

		}

		if ( this.#isMobile || this.#isTablet ) {

			if ( !this.#menuEl.contains( this.#languageSwitcher ) ) {

				this.#menuEl.appendChild( this.#languageSwitcher );

			}

		} else if ( this.#extrasEl && !this.#extrasEl.contains( this.#languageSwitcher ) ) {

			this.#extrasEl.appendChild( this.#languageSwitcher );

		}

	}

	/**
	 * Moves extra buttons to the appropriate location within the menu based on the device type.
	 *
	 * @return {void}
	 */
	#moveExtraButtons () {

		if ( !this.#extrasEl ) {

			return;

		}

		if ( this.#isMobile ) {

			if ( !this.#menuEl.contains( this.#extrasEl ) ) {

				this.#menuEl.appendChild( this.#extrasEl );

			}

		} else {

			const parent = this.#menuEl.parentNode;
			if ( parent ) {

				parent.insertBefore( this.#extrasEl, this.#menuTrigger );

			}

		}

	}

	/**
	 * Handles adding/removing the 'scrolled' class to the body element based on the scroll position.
	 * Only runs the scroll handling logic if scrolling is not already in progress.
	 * Uses requestAnimationFrame to efficiently handle scroll events.
	 *
	 * @return {void}
	 */
	#handleScrollClass () {

		if ( this.#isScrolling ) {

			return;

		}
		this.#isScrolling = true;

		requestAnimationFrame( () => {

			if ( window.scrollY >= 30 ) {

				document.body.classList.add( 'scrolled' );

			} else if ( !document.body.classList.contains( 'lock' ) ) {

				document.body.classList.remove( 'scrolled' );

			}
			this.#isScrolling = false;

		} );

	}

	/**
	 * Sets the body padding CSS variable based on the height of the header element.
	 *
	 * @return {void}
	 */
	#setBodyPadding () {

		document.body.style.setProperty(
			'--header-height',
			`${this.#headerEl.scrollHeight}px`
		);

	}

	/**
	 * Locks the scroll position by preventing scrolling and fixing the body position.
	 * Saves the current scroll position before locking.
	 *
	 * @return {void}
	 */
	#lockScroll () {

		this.#scrollPosition = window.scrollY;
		document.body.style.overflow = 'hidden';
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this.#scrollPosition}px`;
		document.body.style.bottom = '0px';
		document.body.style.width = '100%';
		document.body.classList.add( 'lock' );
		document.documentElement.classList.add( 'lock' );

	}

	/**
	 * Unlock scroll behavior by resetting styles and scroll position of the document.
	 * Removes CSS properties set to prevent scrolling, repositions the scroll to the
	 * previously saved scroll position, and removes lock classes from body and HTML element.
	 *
	 * @return {void}
	 */
	#unlockScroll () {

		document.body.style.removeProperty( 'overflow' );
		document.body.style.removeProperty( 'position' );
		document.body.style.removeProperty( 'top' );
		document.body.style.removeProperty( 'bottom' );
		document.body.style.removeProperty( 'width' );
		window.scrollTo( 0, this.#scrollPosition );
		document.body.classList.remove( 'lock' );
		document.documentElement.classList.remove( 'lock' );

	}

	/**
	 * Handles clicks on the body to close mega menus and menu.
	 *
	 * @param {Event} e - The click event object.
	 * @return {void}
	 */
	#handleBodyClick ( e ) {

		if (
			this.#menuEl.contains( e.target ) ||
			this.#menuTrigger.contains( e.target )
		) {

			return;

		}
		this.#closeAllMegaMenus();
		this.#closeMenu();

	}

	/**
	 * Handles key down event to close all mega menus and the main menu when 'Escape' or 'Esc' key is pressed.
	 *
	 * @param {KeyboardEvent} e - The keyboard event object triggering the key down event.
	 *
	 * @return {void}
	 */
	#handleKeyDown ( e ) {

		if ( e.key === 'Escape' || e.key === 'Esc' ) {

			this.#closeAllMegaMenus();
			this.#closeMenu();

		}

	}

	/**
	 * Opens the menu if it is not already open.
	 * Adds appropriate classes, locks scroll, sets language switcher height,
	 * and closes all mega menus.
	 *
	 * @return {void}
	 */
	#openMenu () {

		if ( this.#menuEl.classList.contains( 'p-header__menu--open' ) ) {

			return;

		}

		if ( this.#headerEl.classList.contains( 'p-header--open' ) ) {

			return;

		}

		this.#menuEl.classList.add( 'p-header__menu--open' );
		this.#headerEl.classList.add( 'p-header--open' );
		this.#menuTrigger.classList.add( 'p-header__mobile-button--open' );

		this.#lockScroll();
		this.#setLanguageSwitcherHeight();
		this.#closeAllMegaMenus();

	}

	/**
	 * Closes the menu by removing the 'p-header__menu--open' class from the menu element
	 * and 'p-header__mobile-button--open' class from the menu trigger element.
	 * Calls the #unlockScroll method to unlock scrolling on the page.
	 * Does nothing if the menu is already closed.
	 *
	 * @return {void}
	 */
	#closeMenu () {

		if ( !this.#menuEl.classList.contains( 'p-header__menu--open' ) ) {

			return;

		}

		if ( !this.#headerEl.classList.contains( 'p-header--open' ) ) {

			return;

		}

		this.#menuEl.classList.remove( 'p-header__menu--open' );
		this.#headerEl.classList.remove( 'p-header--open' );
		this.#menuTrigger.classList.remove( 'p-header__mobile-button--open' );
		this.#unlockScroll();

	}

	/**
	 * Handles the menu trigger action by checking the current state of the menu element.
	 * If the menu element is open, it closes the menu. If the menu element is closed, it opens the menu.
	 *
	 * @return {void}
	 */
	#handleMenuTrigger () {

		if ( this.#menuEl.classList.contains( 'p-header__menu--open' ) ) {

			this.#closeMenu();

		} else {

			this.#openMenu();

		}

	}


	/**
	 * Set the height of the language switcher based on the height of the list elements.
	 *
	 * This method calculates the total height of the language switcher list elements
	 * and sets the custom CSS variable '--lang-list-total-height' accordingly.
	 *
	 * @return {void}
	 */
	#setLanguageSwitcherHeight () {

		const languageSwitcher = this.#headerEl.querySelector( '.language-switcher' );
		if ( !languageSwitcher ) {

			return;

		}

		const list = languageSwitcher.querySelector( '.language-switcher__list' );
		if ( list ) {

			languageSwitcher.style.setProperty(
				'--lang-list-total-height',
				`${list.scrollHeight}px`
			);

		}

	}

	/**
	 * Debounces a function to limit the rate it is called.
	 *
	 * @param {Function} func - The function to be debounced.
	 * @param {number} wait - The amount of time to wait before calling the function.
	 *
	 * @return {Function} Returns a debounced function that delays calling the original function until after a specific time has elapsed without any further calls.
	 */
	#debounce ( func, wait ) {

		let timeout;
		return ( ...args ) => {

			clearTimeout( timeout );
			timeout = setTimeout( () => func( ...args ), wait );

		};

	}

}

